import type {MatchEvent} from '@/components/wyscout/MatchEvents/types';
import api from '@/services/api/client';
import type {WyscoutPlayerFixture} from '@/services/api/wyscout';
import {capabilityValues, Capability, getV2Error} from '@/services/api/v2';

export type WyscoutFixtureEventStatus = 'available' | 'fetched' | 'unavailable';

export interface WyscoutFixtureAvailability {
  code: string;
  message: string;
  retryable: boolean;
}

export type WyscoutFixtureSectionStatus = 'available' | 'unavailable';
export type WyscoutFixtureMatchCacheState = 'available' | 'fetched' | 'unavailable';
export interface WyscoutFixtureLineupPlayer {playerId: number; shirtNumber?: string; position?: string}
export interface WyscoutFixtureTeamLineup {
  teamId: number;
  formation?: string;
  lineup: WyscoutFixtureLineupPlayer[];
  bench: WyscoutFixtureLineupPlayer[];
}
export interface WyscoutFixtureSubstitution {teamId: number; minute: number; playerInId: number; playerOutId: number}

export interface WyscoutPlayerFixtureDetail {
  player: {id: string; wyscoutId: number; name: string};
  playerNames: Record<number, string>;
  fixture: WyscoutPlayerFixture;
  match: {matchId: number; homeTeamId: number; awayTeamId: number; status: string; duration: string};
  events: MatchEvent[];
  eventStatus: WyscoutFixtureEventStatus;
  eventAvailability?: WyscoutFixtureAvailability;
  matchSynchronizedAt: string | null;
  matchCacheState: WyscoutFixtureMatchCacheState;
  lineupStatus: WyscoutFixtureSectionStatus;
  lineups: WyscoutFixtureTeamLineup[];
  substitutionStatus: WyscoutFixtureSectionStatus;
  substitutions: WyscoutFixtureSubstitution[];
  capabilities: Capability[];
}

type UnknownRecord = Record<string, unknown>;

const own = (value: UnknownRecord, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);

const object = (value: unknown, required: readonly string[], optional: readonly string[] = []): UnknownRecord => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Error('Invalid fixture response');
  const result = value as UnknownRecord;
  const allowed = new Set([...required, ...optional]);
  if (required.some(key => !own(result, key)) || Object.keys(result).some(key => !allowed.has(key))) {
    throw new Error('Invalid fixture response');
  }
  return result;
};

const array = (value: unknown): unknown[] => {
  if (!Array.isArray(value)) throw new Error('Invalid fixture response');
  return value;
};

const string = (value: unknown): string => {
  if (typeof value !== 'string') throw new Error('Invalid fixture response');
  return value;
};

const boolean = (value: unknown): boolean => {
  if (typeof value !== 'boolean') throw new Error('Invalid fixture response');
  return value;
};

const finite = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error('Invalid fixture response');
  return value;
};

const safeInteger = (value: unknown): number => {
  const result = finite(value);
  if (!Number.isSafeInteger(result)) throw new Error('Invalid fixture response');
  return result;
};

const integer = (value: unknown, positive = false): number => {
  const result = safeInteger(value);
  if (positive ? result <= 0 : result < 0) throw new Error('Invalid fixture response');
  return result;
};

const oneOf = <T extends string>(value: unknown, values: readonly T[]): T => {
  const result = string(value);
  if (!values.includes(result as T)) throw new Error('Invalid fixture response');
  return result as T;
};

const nullable = <T>(value: unknown, map: (item: unknown) => T): T | null => value === null ? null : map(value);
const optional = <T>(value: UnknownRecord, key: string, map: (item: unknown) => T): T | undefined =>
  own(value, key) ? map(value[key]) : undefined;

const playerNames = (value: unknown): Record<number, string> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Error('Invalid fixture response');
  const names: Record<number, string> = {};
  for (const [key, rawName] of Object.entries(value)) {
    const id = Number(key);
    if (!/^[1-9]\d*$/.test(key) || !Number.isSafeInteger(id) || String(id) !== key || typeof rawName !== 'string') {
      throw new Error('Invalid fixture response');
    }
    const name = rawName.trim();
    if (!name) throw new Error('Invalid fixture response');
    names[id] = name;
  }
  return names;
};

const player = (value: unknown) => {
  const dto = object(value, ['id'], ['name', 'position']);
  return {id: integer(dto.id, true), ...optional(dto, 'name', name => ({name: string(name)})),
    ...optional(dto, 'position', position => ({position: string(position)}))};
};

const team = (value: unknown) => {
  const dto = object(value, ['id'], ['name', 'formation']);
  return {id: integer(dto.id, true), ...optional(dto, 'name', name => ({name: string(name)})),
    ...optional(dto, 'formation', formation => ({formation: string(formation)}))};
};

const location = (value: unknown) => {
  const dto = object(value, ['x', 'y']);
  const result = {x: integer(dto.x), y: integer(dto.y)};
  if (result.x > 100 || result.y > 100) throw new Error('Invalid fixture response');
  return result;
};

const matchEvent = (value: unknown, matchId: number): MatchEvent => {
  const dto = object(value, ['id', 'matchId', 'matchPeriod', 'minute', 'second', 'videoTimestamp', 'type'], [
    'matchTimestamp', 'location', 'player', 'team', 'opponentTeam', 'pass', 'shot', 'groundDuel', 'aerialDuel',
    'infraction', 'carry', 'possession', 'relatedEventId',
  ]);
  const eventMatchId = integer(dto.matchId, true);
  if (eventMatchId !== matchId) throw new Error('Invalid fixture response');
  const typeDTO = object(dto.type, ['primary'], ['secondary']);
  const result: MatchEvent = {
    id: integer(dto.id, true), matchId: eventMatchId, matchPeriod: string(dto.matchPeriod),
    minute: integer(dto.minute), second: integer(dto.second), videoTimestamp: string(dto.videoTimestamp),
    type: {primary: string(typeDTO.primary), ...optional(typeDTO, 'secondary', values => ({secondary: array(values).map(string)}))},
  };
  if (own(dto, 'matchTimestamp')) result.matchTimestamp = string(dto.matchTimestamp);
  if (own(dto, 'location')) result.location = location(dto.location);
  if (own(dto, 'player')) result.player = player(dto.player);
  if (own(dto, 'team')) result.team = team(dto.team);
  if (own(dto, 'opponentTeam')) result.opponentTeam = team(dto.opponentTeam);
  if (own(dto, 'relatedEventId')) result.relatedEventId = integer(dto.relatedEventId, true);
  if (own(dto, 'pass')) {
    const pass = object(dto.pass, ['accurate'], ['angle', 'length', 'height', 'endLocation', 'recipient']);
    result.pass = {accurate: boolean(pass.accurate)};
    if (own(pass, 'angle')) result.pass.angle = safeInteger(pass.angle);
    if (own(pass, 'length')) result.pass.length = finite(pass.length);
    if (own(pass, 'height')) result.pass.height = string(pass.height);
    if (own(pass, 'endLocation')) result.pass.endLocation = location(pass.endLocation);
    if (own(pass, 'recipient')) result.pass.recipient = player(pass.recipient);
  }
  if (own(dto, 'shot')) {
    const shot = object(dto.shot, ['onTarget', 'isGoal'], [
      'bodyPart', 'goalZone', 'xg', 'postShotXg', 'goalkeeperActionId', 'goalkeeper',
    ]);
    result.shot = {onTarget: boolean(shot.onTarget), isGoal: boolean(shot.isGoal)};
    if (own(shot, 'bodyPart')) result.shot.bodyPart = string(shot.bodyPart);
    if (own(shot, 'goalZone')) result.shot.goalZone = string(shot.goalZone);
    if (own(shot, 'xg')) result.shot.xg = finite(shot.xg);
    if (own(shot, 'postShotXg')) result.shot.postShotXg = finite(shot.postShotXg);
    if (own(shot, 'goalkeeperActionId')) result.shot.goalkeeperActionId = integer(shot.goalkeeperActionId, true);
    if (own(shot, 'goalkeeper')) result.shot.goalkeeper = player(shot.goalkeeper);
  }
  if (own(dto, 'groundDuel')) {
    const duel = object(dto.groundDuel, [], [
      'opponent', 'duelType', 'keptPossession', 'progressedWithBall', 'recoveredPossession', 'stoppedProgress',
      'takeOn', 'side', 'relatedDuelId',
    ]);
    result.groundDuel = {};
    if (own(duel, 'opponent')) result.groundDuel.opponent = player(duel.opponent);
    if (own(duel, 'duelType')) result.groundDuel.duelType = string(duel.duelType);
    for (const key of ['keptPossession', 'progressedWithBall', 'recoveredPossession', 'stoppedProgress', 'takeOn'] as const) {
      if (own(duel, key)) result.groundDuel[key] = boolean(duel[key]);
    }
    if (own(duel, 'side')) result.groundDuel.side = string(duel.side);
    if (own(duel, 'relatedDuelId')) result.groundDuel.relatedDuelId = integer(duel.relatedDuelId, true);
  }
  if (own(dto, 'aerialDuel')) {
    const duel = object(dto.aerialDuel, ['firstTouch'], ['opponent', 'height', 'relatedDuelId']);
    result.aerialDuel = {firstTouch: boolean(duel.firstTouch)};
    if (own(duel, 'opponent')) result.aerialDuel.opponent = player(duel.opponent);
    if (own(duel, 'height')) result.aerialDuel.height = integer(duel.height);
    if (own(duel, 'relatedDuelId')) result.aerialDuel.relatedDuelId = integer(duel.relatedDuelId, true);
  }
  if (own(dto, 'infraction')) {
    const infraction = object(dto.infraction, ['yellowCard', 'redCard'], ['opponent', 'type']);
    result.infraction = {yellowCard: boolean(infraction.yellowCard), redCard: boolean(infraction.redCard)};
    if (own(infraction, 'opponent')) result.infraction.opponent = player(infraction.opponent);
    if (own(infraction, 'type')) result.infraction.type = string(infraction.type);
  }
  if (own(dto, 'carry')) {
    const carry = object(dto.carry, [], ['progression', 'endLocation']);
    result.carry = {};
    if (own(carry, 'progression')) result.carry.progression = finite(carry.progression);
    if (own(carry, 'endLocation')) result.carry.endLocation = location(carry.endLocation);
  }
  if (own(dto, 'possession')) {
    const possession = object(dto.possession, ['id', 'eventIndex', 'eventsNumber'], [
      'duration', 'startLocation', 'endLocation', 'team', 'types', 'attack',
    ]);
    result.possession = {
      id: integer(possession.id, true), eventIndex: integer(possession.eventIndex),
      eventsNumber: integer(possession.eventsNumber),
    };
    if (own(possession, 'duration')) result.possession.duration = string(possession.duration);
    if (own(possession, 'startLocation')) result.possession.startLocation = location(possession.startLocation);
    if (own(possession, 'endLocation')) result.possession.endLocation = location(possession.endLocation);
    if (own(possession, 'team')) result.possession.team = team(possession.team);
    if (own(possession, 'types')) result.possession.types = array(possession.types).map(string);
    if (own(possession, 'attack')) {
      const attack = object(possession.attack, ['withShot', 'withShotOnGoal', 'withGoal'], ['flank', 'xg']);
      result.possession.attack = {
        withShot: boolean(attack.withShot), withShotOnGoal: boolean(attack.withShotOnGoal), withGoal: boolean(attack.withGoal),
      };
      if (own(attack, 'flank')) result.possession.attack.flank = string(attack.flank);
      if (own(attack, 'xg')) result.possession.attack.xg = finite(attack.xg);
    }
  }
  return result;
};

const fixture = (value: unknown): WyscoutPlayerFixture => {
  const keys = [
    'matchId', 'detailsAvailable', 'dateUtc', 'label', 'playerTeamId', 'playerTeamName', 'opponentTeamId',
    'opponentTeamName', 'competitionId', 'competitionName', 'seasonId', 'seasonName', 'venue', 'playerScore',
    'opponentScore', 'result', 'participationStatus', 'statsStatus', 'excluded', 'appearances', 'minutes', 'goals',
    'assists', 'shots', 'shotsOnTarget', 'xg', 'xgAssist', 'passes', 'successfulPasses', 'duels', 'duelsWon',
    'defensiveActions', 'interceptions', 'recoveries', 'yellowCards', 'redCards', 'position', 'failureCode', 'failureMessage',
  ] as const;
  const dto = object(value, keys);
  const nullableInteger = (key: typeof keys[number]) => nullable(dto[key], item => integer(item));
  const nullableNumber = (key: typeof keys[number]) => nullable(dto[key], finite);
  const nullableString = (key: typeof keys[number]) => nullable(dto[key], string);
  return {
    matchId: integer(dto.matchId, true), detailsAvailable: boolean(dto.detailsAvailable), dateUtc: string(dto.dateUtc),
    label: nullableString('label'), playerTeamId: nullable(dto.playerTeamId, item => integer(item, true)),
    playerTeamName: nullableString('playerTeamName'), opponentTeamId: nullable(dto.opponentTeamId, item => integer(item, true)),
    opponentTeamName: nullableString('opponentTeamName'), competitionId: integer(dto.competitionId, true),
    competitionName: nullableString('competitionName'), seasonId: integer(dto.seasonId, true), seasonName: nullableString('seasonName'),
    venue: nullable(dto.venue, item => oneOf(item, ['home', 'away', 'neutral'] as const)),
    playerScore: nullableInteger('playerScore'), opponentScore: nullableInteger('opponentScore'),
    result: nullable(dto.result, item => oneOf(item, ['win', 'draw', 'loss'] as const)),
    participationStatus: oneOf(dto.participationStatus, ['played', 'did_not_play'] as const),
    statsStatus: oneOf(dto.statsStatus, ['available', 'unavailable', 'failed', 'ignored'] as const),
    excluded: boolean(dto.excluded), appearances: nullableInteger('appearances'), minutes: nullableInteger('minutes'),
    goals: nullableInteger('goals'), assists: nullableInteger('assists'), shots: nullableInteger('shots'),
    shotsOnTarget: nullableInteger('shotsOnTarget'), xg: nullableNumber('xg'), xgAssist: nullableNumber('xgAssist'),
    passes: nullableInteger('passes'), successfulPasses: nullableInteger('successfulPasses'), duels: nullableInteger('duels'),
    duelsWon: nullableInteger('duelsWon'), defensiveActions: nullableInteger('defensiveActions'),
    interceptions: nullableInteger('interceptions'), recoveries: nullableInteger('recoveries'),
    yellowCards: nullableInteger('yellowCards'), redCards: nullableInteger('redCards'), position: nullableString('position'),
    failureCode: nullableString('failureCode'), failureMessage: nullableString('failureMessage'),
  };
};

const mapDetail = (value: unknown, requestedPlayerId: string, requestedMatchId: number): WyscoutPlayerFixtureDetail => {
  const envelope = object(value, ['data', 'capabilities']);
  const dto = object(envelope.data, [
    'player', 'playerNames', 'fixture', 'match', 'events', 'eventStatus', 'matchSynchronizedAt', 'matchCacheState', 'lineupStatus',
    'lineups', 'substitutionStatus', 'substitutions',
  ], ['eventAvailability']);
  const playerDTO = object(dto.player, ['id', 'wyscoutId', 'name']);
  const matchDTO = object(dto.match, ['matchId', 'homeTeamId', 'awayTeamId', 'status', 'duration']);
  const mappedFixture = fixture(dto.fixture);
  const mappedMatch = {
    matchId: integer(matchDTO.matchId, true), homeTeamId: integer(matchDTO.homeTeamId, true),
    awayTeamId: integer(matchDTO.awayTeamId, true), status: string(matchDTO.status), duration: string(matchDTO.duration),
  };
  const responsePlayerId = string(playerDTO.id);
  if (responsePlayerId !== requestedPlayerId || mappedFixture.matchId !== requestedMatchId || mappedMatch.matchId !== requestedMatchId
    || mappedMatch.homeTeamId === mappedMatch.awayTeamId) throw new Error('Invalid fixture response');
  const eventAvailability = optional(dto, 'eventAvailability', value => {
    const availability = object(value, ['code', 'message', 'retryable']);
    return {code: string(availability.code), message: string(availability.message), retryable: boolean(availability.retryable)};
  });
  const lineupPlayer = (value: unknown): WyscoutFixtureLineupPlayer => {
    const playerDTO = object(value, ['playerId'], ['shirtNumber', 'position']);
    return {playerId: integer(playerDTO.playerId, true),
      ...optional(playerDTO, 'shirtNumber', shirtNumber => ({shirtNumber: string(shirtNumber)})),
      ...optional(playerDTO, 'position', position => ({position: string(position)}))};
  };
  const lineups = array(dto.lineups).map(value => {
    const lineupDTO = object(value, ['teamId', 'lineup', 'bench'], ['formation']);
    return {teamId: integer(lineupDTO.teamId, true),
      ...optional(lineupDTO, 'formation', formation => ({formation: string(formation)})),
      lineup: array(lineupDTO.lineup).map(lineupPlayer), bench: array(lineupDTO.bench).map(lineupPlayer)};
  });
  const substitutions = array(dto.substitutions).map(value => {
    const substitution = object(value, ['teamId', 'minute', 'playerInId', 'playerOutId']);
    const mapped = {teamId: integer(substitution.teamId, true), minute: integer(substitution.minute),
      playerInId: integer(substitution.playerInId, true), playerOutId: integer(substitution.playerOutId, true)};
    if (mapped.playerInId === mapped.playerOutId) throw new Error('Invalid fixture response');
    return mapped;
  });
  return {
    player: {id: responsePlayerId, wyscoutId: integer(playerDTO.wyscoutId, true), name: string(playerDTO.name)},
    playerNames: playerNames(dto.playerNames),
    fixture: mappedFixture, match: mappedMatch,
    events: array(dto.events).map(event => matchEvent(event, requestedMatchId)),
    eventStatus: oneOf(dto.eventStatus, ['available', 'fetched', 'unavailable'] as const),
    ...(eventAvailability ? {eventAvailability} : {}),
    matchSynchronizedAt: nullable(dto.matchSynchronizedAt, string),
    matchCacheState: oneOf(dto.matchCacheState, ['available', 'fetched', 'unavailable'] as const),
    lineupStatus: oneOf(dto.lineupStatus, ['available', 'unavailable'] as const), lineups,
    substitutionStatus: oneOf(dto.substitutionStatus, ['available', 'unavailable'] as const), substitutions,
    capabilities: array(envelope.capabilities).map(value => oneOf(value, capabilityValues)),
  };
};

const rethrow = (error: unknown): never => {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') throw error;
  if (typeof error === 'object' && error !== null) {
    const cancelled = error as UnknownRecord;
    if (cancelled.name === 'CanceledError' || cancelled.code === 'ERR_CANCELED') throw error;
  }
  throw getV2Error(error);
};

export const wyscoutFixtureKeys = {
  detail: (workspaceId: string, playerId: string, wyscoutMatchId: number) =>
    ['v2', 'players', workspaceId, 'detail', playerId, 'wyscout-fixture', wyscoutMatchId] as const,
};

export const wyscoutFixtureAPI = {
  detail: async (
    workspaceId: string,
    playerId: string,
    wyscoutMatchId: number,
    signal?: AbortSignal,
  ): Promise<WyscoutPlayerFixtureDetail> => {
    if (!Number.isSafeInteger(wyscoutMatchId) || wyscoutMatchId <= 0) throw new Error('Invalid Wyscout match ID.');
    try {
      const response = await api.get<unknown>(`/v2/players/${playerId}/wyscout-fixtures/${wyscoutMatchId}`, {
        headers: {'X-Workspace-ID': workspaceId}, signal,
      });
      return mapDetail(response.data, playerId, wyscoutMatchId);
    } catch (error) {
      return rethrow(error);
    }
  },
};
